<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-credit-card me-2" style="color: #22c55e;"></i>{{ $t('Payment Methods') }}
          </h5>
          <small class="text-muted">{{ $t('Finance & Payments') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- Content -->
    <div class="row g-3 mb-4">
      <div class="col-12 col-lg-6">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white border-0 py-3">
            <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-chart-pie me-2 text-success"></i>{{ $t('Payment Methods') }}</h6>
          </div>
          <div class="card-body pt-0">
            <apexchart ref="paymentChart" type="donut" height="300" :options="paymentChartOptions" :series="paymentChartSeries" />
          </div>
        </div>
      </div>
      <div class="col-12 col-lg-6">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white border-0 py-3">
            <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-money-check-dollar me-2 text-primary"></i>{{ $t('Payment Summary') }}</h6>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-sm mb-0 align-middle">
                <thead class="table-light">
                  <tr>
                    <th class="ps-3">{{ $t('Method') }}</th>
                    <th class="text-end">{{ $t('Transactions') }}</th>
                    <th class="text-end">{{ $t('Amount') }}</th>
                    <th class="text-end pe-3">%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!paymentTableData.length && !loading">
                    <td colspan="4" class="text-center text-muted py-4">{{ $t('No data') }}</td>
                  </tr>
                  <tr v-for="pm in paymentTableData" :key="pm.code">
                    <td class="ps-3">
                      <i :class="pm.icon" class="me-2" :style="{ color: pm.color }"></i>{{ pm.label }}
                    </td>
                    <td class="text-end">{{ pm.count }}</td>
                    <td class="text-end fw-semibold">{{ formatCurrency(pm.amount) }}</td>
                    <td class="text-end pe-3">{{ pm.percent }}%</td>
                  </tr>
                </tbody>
                <tfoot v-if="paymentTableData.length" class="table-light fw-bold">
                  <tr>
                    <td class="ps-3">{{ $t('Total') }}</td>
                    <td class="text-end">{{ paymentTotals.count }}</td>
                    <td class="text-end">{{ formatCurrency(paymentTotals.amount) }}</td>
                    <td class="text-end pe-3">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

const PAYMENT_METHOD_META = {
  cash:     { icon: 'fa-solid fa-money-bill-wave', color: '#22c55e' },
  transfer: { icon: 'fa-solid fa-qrcode',          color: '#a855f7' },
  credit:   { icon: 'fa-solid fa-credit-card',     color: '#3b82f6' },
  wallet:   { icon: 'fa-solid fa-wallet',          color: '#06b6d4' },
  delivery: { icon: 'fa-solid fa-motorcycle',      color: '#f97316' },
  other:    { icon: 'fa-solid fa-ellipsis',        color: '#94a3b8' },
};

export default {
  name: 'ReportPaymentMethods',
  components: { apexchart: VueApexCharts },
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Payment Methods',
      methods: [],
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.paymentMethods(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter  = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.methods  = payload.methods || [];
    },

    async buildPdfDefinition() {
      await this.$nextTick();
      const chartImg = await this.getChartImage('paymentChart');
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Payment Methods'), style: 'sectionTitle' },
      ];
      if (chartImg) {
        content.push({ image: chartImg, width: 300, alignment: 'center', margin: [0, 0, 0, 12] });
      }
      content.push(
        { text: this.$t('Payment Summary'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: this.$t('Method'), style: 'tableHeader' },
                { text: this.$t('Transactions'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Amount'), style: 'tableHeader', alignment: 'right' },
                { text: '%', style: 'tableHeader', alignment: 'right' },
              ],
              ...this.paymentTableData.map(pm => [
                { text: pm.label, style: 'tableCell' },
                { text: String(pm.count), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(pm.amount), style: 'tableCell', alignment: 'right' },
                { text: `${pm.percent}%`, style: 'tableCell', alignment: 'right' },
              ]),
              [
                { text: this.$t('Total'), style: 'tableHeader' },
                { text: String(this.paymentTotals.count), style: 'tableHeader', alignment: 'right' },
                { text: this.formatCurrencyRaw(this.paymentTotals.amount), style: 'tableHeader', alignment: 'right' },
                { text: '100%', style: 'tableHeader', alignment: 'right' },
              ],
            ],
          },
          layout: 'lightHorizontalLines',
        },
      );
      return { content };
    },
  },
  computed: {
    paymentTableData() {
      return (this.methods || []).map(m => ({
        ...m,
        icon:  PAYMENT_METHOD_META[m.code]?.icon  || 'fa-solid fa-circle',
        color: PAYMENT_METHOD_META[m.code]?.color || '#94a3b8',
      }));
    },
    paymentTotals() {
      return this.paymentTableData.reduce((acc, p) => ({
        count: acc.count + (p.count || 0),
        amount: acc.amount + (p.amount || 0),
      }), { count: 0, amount: 0 });
    },
    paymentChartSeries() {
      return this.paymentTableData.map(p => p.amount || 0);
    },
    paymentChartOptions() {
      return {
        chart: { fontFamily: 'Sarabun, sans-serif' },
        labels: this.paymentTableData.map(p => p.label),
        colors: this.paymentTableData.map(p => p.color),
        legend: { position: 'bottom' },
        dataLabels: { enabled: true, formatter: v => `${Number(v).toFixed(0)}%` },
        plotOptions: {
          pie: {
            donut: { size: '65%', labels: { show: true, total: { show: true, label: this.$t('Total'), formatter: () => this.formatCurrency(this.paymentTotals.amount) } } },
          },
        },
        tooltip: { y: { formatter: v => this.formatCurrency(v) } },
      };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
